// Utility functions for working with empresa data in the multi-company system

import { useAuthStore } from '../stores/useAuthStore';
import { useCompanyStore } from '../stores/useCompanyStore';
import { supabase } from '../lib/supabase';

/**
 * Get the current empresa ID from the database (usuarios table)
 * This is the SOURCE OF TRUTH - no localStorage, fetches from database
 */
export const getCurrentEmpresaId = async (): Promise<string | null> => {
  try {
    // Get current authenticated user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError || !user) {
      console.warn('⚠️ No authenticated user found');
      return null;
    }
    
    console.log('🔍 Fetching empresa_id for user:', user.email);
    
    // Fetch user's empresa_id from usuarios table (SOURCE OF TRUTH)
    const { data: usuarioData, error: usuarioError } = await supabase
      .from('usuarios')
      .select('empresa_id, nombre, apellido')
      .eq('email', user.email)  // usuarios table uses email, not auth_id
      .single();
    
    if (usuarioError || !usuarioData) {
      console.error('❌ Error fetching usuario from database:', usuarioError);
      return null;
    }
    
    if (!usuarioData.empresa_id) {
      console.warn('⚠️ User has no empresa_id in database');
      return null;
    }
    
    console.log('✅ Empresa ID from database:', usuarioData.empresa_id);
    return usuarioData.empresa_id;
    
  } catch (error) {
    console.error('❌ Error in getCurrentEmpresaId:', error);
    return null;
  }
};

/**
 * SYNCHRONOUS version - gets from Zustand store (which was populated from DB)
 * Use this for components that need immediate access
 */
export const getCurrentEmpresaIdSync = (): string | null => {
  const { currentEmpresa: companyCurrentEmpresa } = useCompanyStore.getState();
  const { usuario } = useAuthStore.getState();
  
  // Try company store first
  if (companyCurrentEmpresa?.id && companyCurrentEmpresa.id !== 'sin-empresa') {
    return companyCurrentEmpresa.id;
  }
  
  // Try usuario.empresaId if it exists
  if (usuario?.empresaId && usuario.empresaId !== 'sin-empresa') {
    return usuario.empresaId;
  }
  
  console.warn('⚠️ No empresa ID in Zustand store - need to fetch from database');
  return null;
};

/**
 * Check if user has a valid empresa
 */
export const hasValidEmpresa = (): boolean => {
  const empresaId = getCurrentEmpresaIdSync();
  return empresaId !== null && empresaId !== 'sin-empresa';
};

/**
 * Get the current empresa object
 */
export const getCurrentEmpresa = () => {
  const { usuario } = useAuthStore.getState();
  return usuario?.currentEmpresa || null;
};

/**
 * Get all user's empresas
 */
export const getUserEmpresas = () => {
  const { usuario } = useAuthStore.getState();
  return usuario?.userEmpresas || [];
};

/**
 * Legacy compatibility function - returns the sync version
 * This can be used to gradually migrate existing code
 */
export const getEmpresaId = getCurrentEmpresaIdSync;




